// src/pages/ConsultaCodigoPenal/index.js
import React, { useState } from 'react';
import axios from 'axios';
import AnonimizadorJuridico from '../../utils/anonimizador';
import './ConsultaCodigoPenal.css';

const ConsultaCodigoPenal = () => {
  const [casoJuridico, setCasoJuridico] = useState('');
  const [casoJuridicoOriginal, setCasoJuridicoOriginal] = useState('');
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [anonimizacaoAtiva, setAnonimizacaoAtiva] = useState(true);
  const [dadosSensiveisDetectados, setDadosSensiveisDetectados] = useState(false);
  const [tiposDadosDetectados, setTiposDadosDetectados] = useState([]);

  const anonimizador = new AnonimizadorJuridico();

  const handleCasoJuridicoChange = (e) => {
    const novoTexto = e.target.value;
    setCasoJuridicoOriginal(novoTexto);
    
    // Verifica se há dados sensíveis
    const contemSensiveis = anonimizador.contemDadosSensiveis(novoTexto);
    const tiposDetectados = anonimizador.tiposDadosEncontrados(novoTexto);
    
    setDadosSensiveisDetectados(contemSensiveis);
    setTiposDadosDetectados(tiposDetectados);
    
    // Aplica anonimização se estiver ativa
    if (anonimizacaoAtiva && contemSensiveis) {
      setCasoJuridico(anonimizador.anonimizar(novoTexto));
    } else {
      setCasoJuridico(novoTexto);
    }
  };

  const toggleAnonimizacao = () => {
    const novaAnonimizacao = !anonimizacaoAtiva;
    setAnonimizacaoAtiva(novaAnonimizacao);
    
    // Reaplica o texto com ou sem anonimização
    if (novaAnonimizacao && dadosSensiveisDetectados) {
      setCasoJuridico(anonimizador.anonimizar(casoJuridicoOriginal));
    } else {
      setCasoJuridico(casoJuridicoOriginal);
    }
  };

  const executarConsulta = async () => {
    if (!casoJuridico.trim()) {
      setError('Por favor, descreva o caso jurídico');
      return;
    }

    setLoading(true);
    setError('');
    setResultado(null);

    try {
      // Sempre envia o texto anonimizado se a anonimização estiver ativa
      const textoParaEnvio = anonimizacaoAtiva ? 
        anonimizador.anonimizar(casoJuridicoOriginal) : 
        casoJuridico;

      const response = await axios.post('/api/codigopenal/consultar', {
        query: textoParaEnvio.trim()
      });

      if (response.data.success) {
        setResultado(response.data.data.results);
      } else {
        setError(response.data.message || 'Erro ao realizar análise');
      }
    } catch (error) {
      console.error('Error analyzing case:', error);
      setError('Erro ao analisar caso: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      executarConsulta();
    }
  };

  const copiarParaAreaTransferencia = () => {
    if (!resultado) return;
    
    const textoParaCopiar = JSON.stringify(resultado, null, 2);
    navigator.clipboard.writeText(textoParaCopiar).then(() => {
      // Feedback visual temporário
      const button = document.querySelector('.copy-btn');
      const originalText = button.textContent;
      button.textContent = '✅ Copiado!';
      button.style.background = 'linear-gradient(135deg, #27ae60, #2ecc71)';
      
      setTimeout(() => {
        button.textContent = originalText;
        button.style.background = 'linear-gradient(135deg, #3498db, #2980b9)';
      }, 2000);
    }).catch(err => {
      console.error('Erro ao copiar:', err);
      setError('Erro ao copiar para área de transferência');
    });
  };

  const downloadComoTxt = () => {
    if (!resultado) return;
    
    const textoParaDownload = JSON.stringify(resultado, null, 2);
    const blob = new Blob([textoParaDownload], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analise_juridica_${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderAlertaDadosSensiveis = () => {
    if (!dadosSensiveisDetectados) return null;

    const nomesAmigaveis = {
      telemovel: 'Telemóveis',
      cartaoCidadao: 'Cartões de Cidadão',
      nif: 'NIFs',
      niss: 'NISS',
      numeroDocumento: 'Números de Documento',
      matricula: 'Matrículas',
      email: 'E-mails',
      endereco: 'Endereços',
      codigoPostal: 'Códigos Postais',
      iban: 'IBANs'
    };

    return (
      <div className="alerta-dados-sensiveis">
        <div className="alerta-header">
          <span className="alerta-icon">⚠️</span>
          <strong>Dados Sensíveis Detectados</strong>
        </div>
        <div className="alerta-content">
          <p>Foram identificados os seguintes tipos de dados sensíveis:</p>
          <ul>
            {tiposDadosDetectados.map(tipo => (
              <li key={tipo}>{nomesAmigaveis[tipo] || tipo}</li>
            ))}
          </ul>
          {anonimizacaoAtiva ? (
            <p className="status-anonimizacao ativo">
              ✅ Anonimização está <strong>ATIVA</strong> - Os dados serão protegidos antes do envio
            </p>
          ) : (
            <p className="status-anonimizacao inativo">
              ❌ Anonimização está <strong>INATIVA</strong> - Os dados serão enviados sem proteção
            </p>
          )}
        </div>
      </div>
    );
  };

  const renderResultado = () => {
    if (!resultado) return null;

    return (
      <div className="resultado-container">
        <div className="resultado-actions">
          <h3>📋 Resultado da Análise</h3>
          <div className="action-buttons">
            <button className="action-btn copy-btn" onClick={copiarParaAreaTransferencia}>
              📋 COPIAR
            </button>
            <button className="action-btn download-btn" onClick={downloadComoTxt}>
              💾 DOWNLOAD .TXT
            </button>
          </div>
        </div>
        
        <div className="resultado-content">
          <textarea
            className="resultado-json"
            value={JSON.stringify(resultado, null, 2)}
            readOnly
            rows={25}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="consulta-codigo-penal-container">
      <div className="consulta-codigo-penal-header">
        <h2>⚖️ Análise Jurídica</h2>
        <p>Análise estratégica de casos jurídicos com base nas leis de Portugal.</p>
      </div>

      <div className="consulta-codigo-penal-content">
        <div className="consulta-form">
          <div className="anonimizacao-controls">
            <div className="toggle-container">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={anonimizacaoAtiva}
                  onChange={toggleAnonimizacao}
                  className="toggle-checkbox"
                />
                <span className="toggle-slider"></span>
                <span className="toggle-text">
                  🔒 Anonimização Automática {anonimizacaoAtiva ? 'ATIVA' : 'INATIVA'}
                </span>
              </label>
            </div>
            <div className="anonimizacao-info">
              Remove automaticamente dados sensíveis (telemóveis, documentos, etc.) antes do envio
            </div>
          </div>

          {renderAlertaDadosSensiveis()}

          <div className="form-group">
            <label className="field-label">
              <span>📝</span>
              Caso Jurídico
            </label>
            <textarea
              value={casoJuridico}
              onChange={handleCasoJuridicoChange}
              onKeyPress={handleKeyPress}
              placeholder="Descreva detalhadamente o caso jurídico para análise estratégica..."
              className="caso-juridico-textarea"
              rows={4}
            />
            <div className="textarea-hint">
              Pressione Ctrl+Enter para analisar
            </div>
          </div>

          <button 
            className="analisar-btn"
            onClick={executarConsulta}
            disabled={loading || !casoJuridico.trim()}
          >
            {loading ? '⏳ Analisando...' : '🔍 ANALISAR CASO'}
          </button>

          {error && <div className="error-message">{error}</div>}
        </div>

        {loading ? (
          <div className="loading-analise">
            <span>⏳</span>
            <span>Analisando caso jurídico...</span>
          </div>
        ) : (
          renderResultado()
        )}
      </div>
    </div>
  );
};

export default ConsultaCodigoPenal;